import { PersonaForm } from '@/components/admin/personas/persona-form';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession } from '@/lib/admin-session';
import { fetchInvolvementLevels, fetchPersonaTones, personasApi } from '@/lib/api';
import type { PersonaDraft } from '@/lib/persona-draft';
import { personaDraftKit } from '@/lib/persona-draft';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import type {
  PersonaContent,
  PersonaDetail,
  PersonaDraftContent,
  PersonaVersion,
  VersionSummary,
} from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

/**
 * L'éditeur d'un persona : le patron de §4, plus le formulaire de §10.
 *
 * Everything this screen does that the criterion's editor does identically -
 * the head, the history, « utilisé dans », the retirement, the three boxes, the
 * debounced autosave and its optimistic control, the rename on blur - is
 * `VersionedEditor`. What is left here is the four things only a persona knows:
 * its API, its draft kit, its wording, and its form.
 *
 * ## The scale is read HERE and the editor waits for it
 *
 * `VersionedEditor` seeds its draft with `useState(() => kit.opening(detail))`,
 * once, at mount - the layer's rule against copying a query into state with an
 * effect. A persona's kit is built FROM THE SCALE (see `persona-draft.ts`), so
 * mounting the layer before the scale has landed would seed a new persona on
 * cran « none » and then never correct it: H14 wants the first cran, and there
 * is no second chance to choose it. Hence the spinner below, and it is the one
 * difference of shape between this file and `criterion-editor.tsx`.
 *
 * The tone library is NOT gated the same way: it feeds the tag cloud only, and
 * a cloud that fills a beat later costs nothing - no draft is seeded from it.
 *
 * ## The two cache keys, and the two it shares
 *
 * `['persona', email, id]` holds a `PersonaDetail`; `['personas', email]` holds
 * the card list. Different prefixes on purpose: the trap of issue #16 is two
 * readers agreeing on a key and disagreeing on what is under it, and `useQuery`
 * is generic per component so nothing would catch the drift.
 *
 * `['involvement-levels', email]` and `['persona-tones', email]` are the keys
 * Réglages > Personas already writes, deliberately: same fetcher, same schema,
 * same shape. Sharing them is what makes a cran renamed in the settings tab
 * show up here without a reload; inventing a second key for the same list is
 * the way the two would eventually disagree.
 */

export interface PersonaEditorProps {
  /** The entity, or null for a persona that does not exist yet. */
  detail: PersonaDetail | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: PersonaVersion | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the entity from the server - what the stale-draft box offers. */
  onReload: () => void;
  /**
   * Select the name field on opening (§4.2): true on the editor a duplication
   * has just navigated to, which the route reads off `?copie`.
   */
  selectName?: boolean;
}

export function PersonaEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
  selectName = false,
}: PersonaEditorProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();

  const scale = useQuery({
    queryKey: ['involvement-levels', email],
    queryFn: fetchInvolvementLevels,
    retry: false,
  });

  const library = useQuery({
    queryKey: ['persona-tones', email],
    queryFn: fetchPersonaTones,
    retry: false,
  });

  if (scale.isPending) {
    return (
      <div className="flex justify-center py-12">
        <PmsSpinner label="Chargement de l’échelle d’implication" />
      </div>
    );
  }

  if (scale.data === undefined) {
    return (
      <p className="text-gray-700">
        L’échelle d’implication n’a pas pu être chargée, et l’éditeur ne peut pas s’ouvrir sans elle
        : c’est elle qui donne au persona son cran et sa direction.
      </p>
    );
  }

  const levels = scale.data.levels;

  return (
    <VersionedEditor<PersonaContent, PersonaDraftContent, PersonaDraft>
      wording={PERSONA_WORDING}
      api={personasApi}
      kit={personaDraftKit(levels)}
      keys={{ detail: 'persona', list: 'personas' }}
      hasReferents
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={(personaId) =>
        void navigate({ to: '/admin/personas/$personaId', params: { personaId } })
      }
      selectName={selectName}
      renderFields={(binding) => (
        <PersonaForm
          binding={binding}
          levels={levels}
          tones={library.data?.tones ?? []}
          libraryRead={library.data !== undefined}
        />
      )}
    />
  );
}
